import { useRef } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@/components/ui';
import { cn } from '@/utils/cn';

/**
 * The pieces a dashboard is built from, as distinct from the shell around it.
 *
 * These are presentational only -- none of them fetch, and none of them know
 * which role is looking. A page passes numbers it already has.
 */

// --------------------------------------------------------------------------- //
// Greeting banner
// --------------------------------------------------------------------------- //

/**
 * The gradient band a dashboard opens with.
 *
 * It replaces `PageHeader` on these three pages only. Everywhere else the
 * plain header is still right -- a band this loud on every list screen would
 * stop meaning anything by the third one.
 */
export function GreetingBanner({ name, subtitle, action, className }) {
  return (
    <section
      className={cn(
        'shell-hero rounded-card relative isolate overflow-hidden px-5 py-4 text-white sm:px-6',
        className,
      )}
    >
      {/* Decorative only, and hidden from the tree: two soft discs bled off
          the right edge, which is what stops the gradient reading as a flat
          slab. `isolate` on the parent keeps them off anything above. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-10 -z-10 size-64 rounded-full bg-white/10"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -bottom-32 -z-10 size-72 rounded-full bg-white/5"
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold tracking-tight">{name}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-white/80">{subtitle}</p>}
        </div>
        {action}
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------- //
// Stat ring
// --------------------------------------------------------------------------- //

const RING_SIZE = 64;
const RING_STROKE = 5;
const RADIUS = (RING_SIZE - RING_STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * A number inside a progress ring.
 *
 * `max` is what the ring is a fraction of; when it is unknown or meaningless
 * (a count with no ceiling) pass nothing and the ring draws full, which is the
 * honest reading of "there is no target here".
 */
export function StatRing({ value, max, label, unit, tone = 'brand' }) {
  const numeric = Number(value) || 0;
  const fraction = max ? Math.max(0, Math.min(1, numeric / max)) : 1;
  const stroke = tone === 'brand' ? 'stroke-brand-600' : 'stroke-success-500';

  return (
    <div className="flex min-w-0 flex-col items-center gap-2">
      <div className="relative" style={{ width: RING_SIZE, height: RING_SIZE }}>
        <svg
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          className="-rotate-90"
          aria-hidden="true"
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            className="stroke-brand-100"
          />
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - fraction)}
            className={cn(stroke, 'transition-[stroke-dashoffset] duration-500')}
          />
        </svg>
        <span className="absolute inset-0 grid place-items-center text-center leading-none">
          <span>
            <span className="text-ink block text-lg font-semibold tabular-nums">{value}</span>
            {unit && (
              <span className="text-ink-muted mt-0.5 block text-[10px] font-medium">{unit}</span>
            )}
          </span>
        </span>
      </div>
      <p className="text-ink-muted w-full truncate text-center text-[10px] font-semibold tracking-wide uppercase">
        {label}
      </p>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Rail card
// --------------------------------------------------------------------------- //

/**
 * A card with an icon chip and a title, for the narrow right-hand column.
 *
 * `links` renders the footer row of quiet actions the reference puts under
 * each rail card -- text, not buttons, because a column of filled buttons down
 * the side of a page competes with whatever the page is actually for.
 */
export function RailCard({ icon, title, subtitle, links, children, className }) {
  return (
    <section
      className={cn(
        'border-line bg-surface rounded-card border p-4 shadow-[var(--shadow-card)]',
        className,
      )}
    >
      <div className="flex items-center gap-3">
        {icon && (
          <span className="bg-brand-50 text-brand-600 grid size-8 shrink-0 place-items-center rounded-lg">
            <Icon name={icon} size={16} />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-ink truncate text-sm font-semibold">{title}</h2>
          {subtitle && <p className="text-ink-muted truncate text-xs">{subtitle}</p>}
        </div>
      </div>

      {children && <div className="mt-3">{children}</div>}

      {links?.length > 0 && (
        <div className="border-line mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t pt-2.5">
          {links.map((link) => (
            <Link
              key={`${link.to}-${link.label}`}
              to={link.to}
              className="text-brand-600 hover:text-brand-700 text-xs font-semibold transition"
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

// --------------------------------------------------------------------------- //
// Tabs
// --------------------------------------------------------------------------- //

/**
 * The underlined tab row from the reference: icon and label, a violet
 * underline on the active one, a hairline across the page beneath them all.
 * For switching whole sections of a page ("Saved · For you · Applied");
 * `SegmentedTabs` stays for filtering the list inside a card.
 *
 * Controlled: `onChange(key)` decides what switching means -- a page with a
 * URL per tab navigates. Left/Right/Home/End move between tabs, as the ARIA
 * tabs pattern expects; on a phone the row scrolls sideways.
 */
export function Tabs({ tabs, value, onChange, label, className }) {
  const refs = useRef([]);

  function onKeyDown(event, index) {
    const last = tabs.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    refs.current[next]?.focus();
    onChange(tabs[next].key);
  }

  return (
    <div className={cn('tab-strip tab-strip-rule max-w-full', className)}>
      <div className="flex items-center gap-5" role="tablist" aria-label={label}>
        {tabs.map((tab, index) => {
          const isActive = tab.key === value;
          return (
            <button
              key={tab.key}
              ref={(element) => {
                refs.current[index] = element;
              }}
              type="button"
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(tab.key)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 border-b-2 px-0.5 py-2 text-[13px] font-medium whitespace-nowrap transition',
                'focus-visible:outline-brand-600 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2',
                isActive
                  ? 'border-brand-600 text-brand-600'
                  : 'text-ink-muted hover:text-ink hover:border-line border-transparent',
              )}
            >
              {tab.icon && <Icon name={tab.icon} size={15} />}
              {tab.label}
              {tab.count != null && (
                <span
                  className={cn(
                    'min-w-4 rounded-full px-1.5 text-center text-[10px] leading-4 font-semibold tabular-nums',
                    isActive ? 'bg-brand-50 text-brand-700' : 'bg-surface-muted text-ink-muted',
                  )}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Section heading
// --------------------------------------------------------------------------- //

/** The small label above a block of cards. */
export function SectionHeading({ children, action, className }) {
  return (
    <div className={cn('mb-3 flex items-end justify-between gap-3', className)}>
      <h2 className="text-ink-muted text-[11px] font-semibold tracking-[0.1em] uppercase">
        {children}
      </h2>
      {action}
    </div>
  );
}
